import { FetchArgs } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { selectItemType } from '@src/types';
import {
  CredentialKind,
  CredentialRecord,
  CreatePersonnelRequest,
  PersonnelProfile,
  PersonnelStatusValue,
  SaveCredentialRecordRequest,
  UpdatePersonnelRequest,
} from '@src/types/personnel';
import { toastMessage } from '@utils/toastMessage';
import { getValidity } from '@component/ContractorPortal/Credentialing/shared/helpers';
import { BaseQuery, mockApiError } from './credentialingContract';

type LegacyRecordRow = {
  id: number;
  number?: string | null;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  type?: string | null;
  issuing?: string | null;
  organization?: string | null;
};

const maskLast4 = (value?: string | null) =>
  value ? `***${value.slice(-4)}` : null;

const LEGACY_VALUES: Record<
  CredentialKind,
  (row: LegacyRecordRow) => Record<string, string | null>
> = {
  identification: (row) => ({
    expirationDate: row.expirationDate ?? null,
    issueDate: row.effectiveDate ?? null,
    issuingState: row.issuing ?? null,
    number: maskLast4(row.number),
  }),
  permit: (row) => ({
    expirationDate: row.expirationDate ?? null,
    issueDate: row.effectiveDate ?? null,
    issuingAuthority: row.organization ?? null,
    number: row.number ?? null,
  }),
  screening: (row) => ({
    completionDate: row.effectiveDate ?? null,
    expirationDate: row.expirationDate ?? null,
    vendor: row.organization ?? null,
  }),
  training: (row) => ({
    certificateNumber: row.number ?? null,
    completionDate: row.effectiveDate ?? null,
    expirationDate: row.expirationDate ?? null,
    provider: row.organization ?? null,
  }),
};

const isContractRecord = (row: unknown): row is CredentialRecord =>
  !!row && typeof row === 'object' && 'values' in row;

export const toCredentialRecords = (
  data: unknown,
  kind: CredentialKind,
  personnelId: number
): Array<CredentialRecord> =>
  (Array.isArray(data) ? data : []).map((row) =>
    isContractRecord(row)
      ? row
      : {
          approvals: [],
          files: [],
          id: (row as LegacyRecordRow).id,
          fullNumber:
            kind === 'identification'
              ? ((row as LegacyRecordRow).number ?? null)
              : null,
          kind,
          personnelId,
          type: (row as LegacyRecordRow).type ?? '',
          validity:
            getValidity((row as LegacyRecordRow).expirationDate) ?? null,
          values: LEGACY_VALUES[kind](row as LegacyRecordRow),
        }
  );

type LegacyPersonnel = {
  id: number;
  firstName: string;
  lastName: string;
  dob?: string | null;
  identification?: string | null;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  status?: PersonnelStatusValue;
};

export const toPersonnelProfile = (data: unknown): PersonnelProfile => {
  if (data && typeof data === 'object' && 'dobMasked' in data) {
    return data as PersonnelProfile;
  }

  const person = data as LegacyPersonnel;
  const dob = moment(person.dob, 'MM/DD/YYYY', true);
  const hasDob = !!person.dob && dob.isValid();

  return {
    age: hasDob ? moment().diff(dob, 'years') : '',
    dob: hasDob ? person.dob : null,
    dobMasked: hasDob ? `**/**/${dob.year()}` : '',
    effectiveDate: person.effectiveDate ?? null,
    email: null,
    expirationDate: person.expirationDate ?? null,
    firstName: person.firstName,
    id: person.id,
    identificationNumber: person.identification ?? null,
    identificationNumberMasked: maskLast4(person.identification),
    lastName: person.lastName,
    phone: '',
    profilePictureUrl: null,
    status: person.status ?? null,
  };
};

const optionIdOf = async (
  url: string,
  label: string,
  baseQuery: BaseQuery,
  message: string
) => {
  const { data } = await baseQuery({ url });
  const match = (Array.isArray(data) ? data : []).find(
    (option: selectItemType) =>
      option.label?.trim().toLowerCase() === label.trim().toLowerCase()
  );

  if (!match) throw mockApiError(400, message);

  return match.id;
};

const statusIdOf = (label: string, baseQuery: BaseQuery) =>
  optionIdOf(
    '/v1/personnel/info/status/select',
    label,
    baseQuery,
    toastMessage.CONTRACTOR.personnelStatusUnavailable
  );

export const RECORD_SELECTS: Record<
  CredentialKind,
  { issuerField: string; issuerUrl: string; path: string; typesUrl: string }
> = {
  identification: {
    issuerField: 'issuingState',
    issuerUrl: '/v1/personnel/identification/issuing/select',
    path: 'identification',
    typesUrl: '/v1/personnel/identification/types/select',
  },
  permit: {
    issuerField: 'issuingAuthority',
    issuerUrl: '/v1/personnel/permits/issuing/select',
    path: 'permits',
    typesUrl: '/v1/personnel/permits/types/select',
  },
  screening: {
    issuerField: 'vendor',
    issuerUrl: '/v1/personnel/screenings/issuing/select',
    path: 'screenings',
    typesUrl: '/v1/personnel/screenings/types/select',
  },
  training: {
    issuerField: 'provider',
    issuerUrl: '/v1/personnel/certifications/issuing/select',
    path: 'certifications',
    typesUrl: '/v1/personnel/certifications/types/select',
  },
};

const LEGACY_FIELDS: Record<CredentialKind, Record<string, string>> = {
  identification: {
    expirationDate: 'expirationDate',
    issueDate: 'effectiveDate',
    number: 'number',
  },
  permit: {
    expirationDate: 'expirationDate',
    issueDate: 'effectiveDate',
    number: 'number',
  },
  screening: {
    completionDate: 'effectiveDate',
    expirationDate: 'expirationDate',
  },
  training: {
    certificateNumber: 'number',
    completionDate: 'effectiveDate',
    expirationDate: 'expirationDate',
  },
};

export const toLegacyRecordSave = async (
  { id, kind, personnelId, type, values }: SaveCredentialRecordRequest,
  baseQuery: BaseQuery
): Promise<FetchArgs> => {
  const { issuerField, issuerUrl, path, typesUrl } = RECORD_SELECTS[kind];
  const issuer = values[issuerField];
  const body: Record<string, unknown> = {
    personnelId,
    type: await optionIdOf(
      typesUrl,
      type,
      baseQuery,
      toastMessage.CONTRACTOR.credentialingOptionUnavailable
    ),
    [kind === 'identification' ? 'issuing' : 'organization']: issuer
      ? await optionIdOf(
          issuerUrl,
          issuer,
          baseQuery,
          toastMessage.CONTRACTOR.credentialingOptionUnavailable
        )
      : null,
  };

  Object.entries(LEGACY_FIELDS[kind]).forEach(([field, legacyField]) => {
    const value = values[field];
    if (value !== undefined && !value?.startsWith('***')) {
      body[legacyField] = value;
    }
  });

  return {
    url: `/v1/personnel/${path}/${id ? `update/${id}` : 'create'}`,
    method: id ? 'PUT' : 'POST',
    body,
  };
};

export const toLegacyPersonnelCreate = async (
  person: CreatePersonnelRequest,
  baseQuery: BaseQuery
): Promise<FetchArgs> => ({
  url: '/v1/personnel/personnel/create',
  method: 'POST',
  body: { ...person, status: await statusIdOf(person.status, baseQuery) },
});

export const toLegacyPersonnelUpdate = async (
  { changes, id, note }: UpdatePersonnelRequest,
  baseQuery: BaseQuery
): Promise<FetchArgs> => ({
  url: `/v1/personnel/personnel/update/${id}`,
  method: 'PUT',
  body: {
    ...changes,
    note,
    ...(changes.status && {
      status: await statusIdOf(changes.status, baseQuery),
    }),
  },
});
